<script setup lang="ts">
import {onUpdated, ref} from "vue";

const props = withDefaults(defineProps<{
  step?: number | string;
  title?: string;
  image?: string;
  headerImage?: string;
  headerImageSrcset?: string;
  imageWidthPercent?: number;
  subtitle?: string;
  tipTitle?: string;
  tipText?: string;
  tipImage?: string;
  loading?: boolean;
  open?: boolean;
}>(), {
  step: 1,
  title: '',
  image: '',
  headerImage: '',
  headerImageSrcset: '',
  imageWidthPercent: 30,
  subtitle: '',
  tipTitle: '',
  tipText: '',
  tipImage: '',
  loading: true,
  open: false,
});

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
}>();

const handleToggle = (event: Event) => {
  emit('update:open', (event.target as HTMLDetailsElement).open);
};

// iOS/iPadOS Safari (WebKit) bug: when the DOM content inside an already-open <details>
// element is heavily mutated (nodes added/removed, not just text updates), WebKit silently
// drops the "open" attribute, closing the box even though our "open" prop/ref is still true.
// Desktop browsers (Blink/Gecko) don't have this defect. To work around it we keep a direct
// reference to the <details> element and re-assert its "open" state right after every DOM
// update of this component, which also covers updates caused by reactive data used inside
// the "main"/"more"/"details" slots (e.g. loading state or variant lists toggling).
const detailsRef = ref<HTMLDetailsElement | null>(null);

onUpdated(() => {
  if (detailsRef.value && detailsRef.value.open !== props.open) {
    detailsRef.value.open = props.open;
  }
});

const showMore = ref(false);

const toggleMore = () => {
  showMore.value = !showMore.value;
};

const onBeforeEnter = (el: Element) => {
  (el as HTMLElement).style.height = '0px';
};

const onEnter = (el: Element) => {
  const target = el as HTMLElement;
  requestAnimationFrame(() => {
    target.style.transition = 'height 300ms ease';
    target.style.height = `${target.scrollHeight}px`;
  });
};

const onAfterEnter = (el: Element) => {
  const target = el as HTMLElement;
  target.style.transition = '';
  target.style.height = 'auto';
};

const onBeforeLeave = (el: Element) => {
  const target = el as HTMLElement;
  target.style.height = `${target.scrollHeight}px`;
};

const onLeave = (el: Element) => {
  const target = el as HTMLElement;
  requestAnimationFrame(() => {
    target.style.transition = 'height 300ms ease';
    target.style.height = '0px';
  });
};

const onAfterLeave = (el: Element) => {
  (el as HTMLElement).style.transition = '';
};
</script>

<template>
  <details
      ref="detailsRef"
      :open="open"
      class="group/parameter w-full rounded-2xl border border-white/70 bg-parameter-box-bg text-parameter-text shadow-[0_1px_3px_rgba(68,68,68,0.06)]"
      @toggle="handleToggle"
  >
    <summary
        class="flex min-h-[72px] h-full cursor-pointer list-none items-center rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#8b6536] [&::-webkit-details-marker]:hidden"
    >
      <div class="min-w-0 flex-1 px-[18px] py-3.5">
        <h5 class="m-0 text-sm tracking-tight text-gray-900 font-ivymode">{{ title }}</h5>
        <p class="m-0 mt-1 text-[10px] leading-[15px] font-normal tracking-[0.1px] text-parameter-text truncate">
          {{ subtitle }}
        </p>
      </div>
      <div
          v-if="headerImage"
          class="relative shrink-0 w-28 sm:w-36 overflow-hidden bg-parameter-bg"
      >
        <img
            :src="headerImage"
            :srcset="headerImageSrcset || undefined"
            class="w-full h-full object-cover"
            alt=""
        />
        <div
            class="pointer-events-none absolute inset-0 bg-gradient-to-r from-parameter-box-bg to-transparent w-1/4"></div>
      </div>
      <div class="shrink-0 px-2 py-2.5">
        <svg
            aria-hidden="true"
            viewBox="0 0 12 12"
            fill="none"
            class="size-3 text-[#b7a98f] transition-transform duration-300 group-open/parameter:rotate-90"
        >
          <path d="m4 1.5 4 4.5-4 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </summary>
    <div
        class="relative isolate grid min-h-[263px] grid-cols-1 md:grid-cols-5 px-3.5"
        :style="{ '--image-width': `clamp(0%, ${imageWidthPercent}%, 100%)` }"
    >
      <!-- content and image -->
      <div
          v-if="image && imageWidthPercent > 0"
          class="lg:hidden pointer-events-none col-span-5 md:col-start-1 md:row-start-1 w-full md:w-(--image-width) mx-auto max-w-64 md:mx-0 md:max-w-42 aspect-square self-start z-10 h-full flex items-start overflow-hidden">
        <!-- image -->
        <img :src="image" class="relative md:-left-5 w-full object-contain object-center lazy" alt=""/>
      </div>
      <div class="col-span-5 md:col-start-1 md:row-start-1 z-20 flex flex-col justify-start items-center md:items-end">
        <!-- content -->
        <div
            class="mt-2 grow flex flex-col items-end justify-start w-full md:w-[calc(105%-var(--image-width))] lg:w-11/12">
          <slot name="main"></slot>
          <button
              v-if="$slots.more && !showMore"
              type="button"
              class="flex w-full items-center gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536] mt-2"
              aria-expanded="false"
              @click="toggleMore"
          >
            <span class="h-px flex-1 bg-[#e7ddcc]"></span>
            <span
                class="flex items-center gap-1.5 text-xs font-medium tracking-[0.1px] whitespace-nowrap text-parameter-text cursor-pointer">
              Mehr anzeigen
              <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 text-[#555956]">
                <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="h-px flex-1 bg-[#e7ddcc]"></span>
          </button>
        </div>
        <div v-if="$slots.details" class="flex flex-col w-full mt-5">
          <slot name="details"></slot>
        </div>
        <Transition
            @before-enter="onBeforeEnter"
            @enter="onEnter"
            @after-enter="onAfterEnter"
            @before-leave="onBeforeLeave"
            @leave="onLeave"
            @after-leave="onAfterLeave"
        >
          <div v-if="showMore" class="w-full overflow-hidden">
            <div class="flex flex-col w-full mt-5">
              <slot name="more"></slot>
              <button
                  type="button"
                  class="mt-4 flex w-full items-center gap-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b6536]"
                  aria-expanded="true"
                  @click="toggleMore"
              >
                <span class="h-px flex-1 bg-[#e7ddcc]"></span>
                <span
                    class="flex items-center gap-1.5 text-xs font-medium tracking-[0.1px] whitespace-nowrap text-parameter-text">
                  Weniger anzeigen
                  <svg aria-hidden="true" viewBox="0 0 12 12" fill="none"
                       class="size-2.5 shrink-0 rotate-180 text-[#555956]">
                    <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="h-px flex-1 bg-[#e7ddcc]"></span>
              </button>
            </div>
          </div>
        </Transition>
        <aside
            v-if="tipTitle || tipText"
            class="my-3 mr-auto flex w-full md:max-w-4/5 gap-x-4 items-stretch rounded-[15px] bg-[#f3f0e9] p-3 max-h-20"
        >
          <div class="aspect-square h-full shrink-0 rounded-2xl overflow-hidden max-h-16">
            <img
                :src="tipImage"
                class="h-full w-full object-cover object-center"
                alt=""
            />
          </div>
          <div>
            <h6 v-if="tipTitle" class="m-0 text-[12px] leading-3.5 font-bold text-parameter-title">
              {{ tipTitle }}
            </h6>
            <p v-if="tipText" class="mt-1 text-[9px] leading-[13px] text-parameter-text relative  overflow-hidden h-10">
              {{ tipText }}
            </p>
          </div>
        </aside>
        <aside v-else>
          <br/>
        </aside>

      </div>
      <!-- loading overlay -->
      <div
          v-if="loading"
          class="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 rounded-[11px] bg-parameter-box-bg"
      >
        <span class="relative flex size-14 items-center justify-center">
          <span
              class="absolute size-full rounded-full bg-radial-[at_35%_30%] from-[#a57a43] to-[#805b2f] opacity-25 animate-ping"
          ></span>
          <span
              class="absolute size-11 rounded-full border-[3px] border-[#e7ddcc] border-t-[#8b6536] animate-spin"
          ></span>
          <span
              class="size-4 rounded-full bg-radial-[at_35%_30%] from-[#a57a43] to-[#805b2f] animate-pulse"
          ></span>
        </span>
        <span class="text-[10px] font-medium tracking-[0.1px] text-parameter-text animate-pulse">
          Wird geladen …
        </span>
      </div>
    </div>
  </details>
</template>